import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { formatDate, formatRs } from '../lib/money';
import { loanApi, loanState, perPeriod, periods, weeksToRepay, type LoanRow } from '../lib/loans';

/**
 * Loan requests waiting for the owner, at the top of his Approvals (25
 * September 2026: "even someone request a loan it should come to approval
 * section"). Approve sends it back to the office to hand over the cash and
 * take his signature; Reject voids it with a reason (rule 3: never deleted).
 * Draws nothing when nothing is waiting, like ExpiringSoon.
 */

const POLL_MS = 30_000;

export default function LoanRequests() {
  const [waiting, setWaiting] = useState<LoanRow[]>([]);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await loanApi.list();
      setWaiting(result.loans.filter((loan) => loanState(loan) === 'waiting'));
    } catch {
      // No signal: say nothing. The Loans segment reports its own errors.
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  async function act(id: string, work: () => Promise<unknown>) {
    setBusy(id);
    setError('');
    try {
      await work();
      setRejecting(null);
      setReason('');
      setTried(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  if (waiting.length === 0) return null;

  return (
    <div className="card">
      <h3>Loan requests</h3>
      <div className="sub">
        {waiting.length} waiting for you. Once you approve, the office hands over the cash and he signs for it.
      </div>
      {error && <div className="banner b-no">{error}</div>}

      {waiting.map((loan) => (
        <div key={loan.id} className="row" style={{ flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div className="name">{loan.worker_name} — {formatRs(loan.amount_rs)}</div>
            <div className="sub">
              {formatRs(loan.weekly_rs)} {perPeriod(loan.repay_cycle)} (about {weeksToRepay(loan.amount_rs, loan.weekly_rs)}{' '}
              {periods(loan.repay_cycle, weeksToRepay(loan.amount_rs, loan.weekly_rs))})
              {' · '}{loan.reason}{' · '}asked for by {loan.created_by_name} on {formatDate(loan.given_on)}
            </div>
          </div>

          {rejecting === loan.id ? (
            <div style={{ flexBasis: '100%', marginTop: 6 }}>
              <div className="sub" style={{ marginBottom: 4 }}>
                Why not?
                {tried && reason.trim() === '' && (
                  <span style={{ color: 'var(--text-danger)' }}> — write a reason</span>
                )}
              </div>
              <input
                type="text"
                value={reason}
                placeholder="Already owes too much, ask again next month…"
                onChange={(e) => setReason(e.target.value)}
                style={{
                  width: '100%', textAlign: 'left',
                  ...(tried && reason.trim() === '' ? { borderColor: 'var(--danger)' } : {}),
                }}
              />
              <div className="bar" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn sm dang"
                  disabled={busy === loan.id}
                  onClick={() => {
                    setTried(true);
                    if (reason.trim() === '') return;
                    void act(loan.id, () => loanApi.void(loan.id, reason.trim()));
                  }}
                >
                  Yes, reject it
                </button>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => { setRejecting(null); setReason(''); setTried(false); }}
                >
                  Keep it
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="btn sm pri"
                disabled={busy === loan.id}
                onClick={() => void act(loan.id, () => loanApi.approve(loan.id))}
              >
                Approve
              </button>
              {/* No confirm() — an inline reason instead. */}
              <button
                type="button"
                className="btn sm dang"
                onClick={() => { setRejecting(loan.id); setReason(''); setTried(false); }}
              >
                Reject
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
